import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { COMPANY } from "@/i18n/translations";
import { Mail, Phone, MapPin, ArrowUpRight, Instagram, Facebook } from "lucide-react";

export const Footer = () => {
  const { t } = useLang();

  return (
    <footer className="bg-slate-950 text-slate-200">
      <div className="container-orion py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Big CTA */}
          <div className="lg:col-span-7">
            <div className="text-eyebrow text-blue-400">{t("ctaBanner.eyebrow")}</div>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05] text-white">
              {t("ctaBanner.title")}
            </h2>
            <p className="mt-6 text-slate-400 max-w-xl text-base leading-relaxed">
              {t("ctaBanner.desc")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="footer-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-6 py-3 text-sm font-semibold text-white transition-colors"
              >
                {t("ctaBanner.cta")}
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                data-testid="footer-cta-call"
                href={`tel:${COMPANY.phone}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:bg-white/10 px-6 py-3 text-sm font-semibold text-white transition-colors"
              >
                <Phone className="w-4 h-4" />
                {COMPANY.phoneDisplay}
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-10">
            <div>
              <div className="text-eyebrow text-slate-500">{t("footer.ourServices")}</div>
              <ul className="mt-5 space-y-3 text-sm">
                <li>
                  <Link to="/services/facility-management" className="hover:text-white transition-colors">
                    {t("services.fm.title")}
                  </Link>
                </li>
                <li>
                  <Link to="/services/cleaning" className="hover:text-white transition-colors">
                    {t("services.cleaning.title")}
                  </Link>
                </li>
                <li>
                  <Link to="/services/maintenance" className="hover:text-white transition-colors">
                    {t("services.maintenance.title")}
                  </Link>
                </li>
                <li>
                  <Link to="/services/manpower" className="hover:text-white transition-colors">
                    {t("services.manpower.title")}
                  </Link>
                </li>
                <li>
                  <Link to="/amc-sla" className="hover:text-white transition-colors">
                    {t("amc.sectionLabel")}
                  </Link>
                </li>
                <li>
                  <Link to="/quality-and-safety" data-testid="footer-quality" className="hover:text-white transition-colors">
                    Quality & Safety
                  </Link>
                </li>
                <li>
                  <Link to="/partners" data-testid="footer-partners" className="hover:text-white transition-colors">
                    Partners
                  </Link>
                </li>
                <li>
                  <Link to="/careers" data-testid="footer-careers" className="hover:text-white transition-colors">
                    {t("nav.careers")}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <div className="text-eyebrow text-slate-500">{t("footer.contact")}</div>
              <ul className="mt-5 space-y-3 text-sm text-slate-400">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-slate-500" />
                  {t("contact.address")}
                </li>
                <li>
                  <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="w-4 h-4 text-slate-500" />
                    {COMPANY.phoneDisplay}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="w-4 h-4 text-slate-500" />
                    {COMPANY.email}
                  </a>
                </li>
                <li className="pt-2 flex items-center gap-3">
                  <a
                    href={COMPANY.social.instagram}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-instagram"
                    aria-label="Instagram"
                    className="w-9 h-9 rounded-full bg-white/5 hover:bg-blue-600 border border-white/10 flex items-center justify-center transition-colors"
                  >
                    <Instagram className="w-4 h-4" />
                  </a>
                  <a
                    href={COMPANY.social.facebook}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-facebook"
                    aria-label="Facebook"
                    className="w-9 h-9 rounded-full bg-white/5 hover:bg-blue-600 border border-white/10 flex items-center justify-center transition-colors"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                </li>
              </ul>

              <div className="mt-8">
                <div className="text-eyebrow text-slate-500">{t("footer.legal")}</div>
                <ul className="mt-5 space-y-3 text-sm text-slate-400">
                  <li>
                    <Link to="/privacy" className="hover:text-white transition-colors">
                      {t("footer.privacy")}
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms" className="hover:text-white transition-colors">
                      {t("footer.terms")}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 text-sm text-slate-500">
          <div className="flex items-center gap-3">
            <img
              src={COMPANY.logo}
              alt="Orion"
              className="w-9 h-9 rounded-lg object-cover bg-slate-900 shrink-0"
            />
            <div className="leading-tight">
              <div className="text-white font-heading font-medium">{COMPANY.brandFull}</div>
              <div className="text-xs">{COMPANY.domain}</div>
            </div>
          </div>
          <div className="text-end">
            <div>{t("footer.copyright")}</div>
            <div className="mt-1 text-xs">{t("footer.built")}</div>
          </div>
        </div>
      </div>
    </footer>
  );
};
